import API from '../config/api';
import React, { useState, useEffect, useCallback } from 'react';

import axios from 'axios';

import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup
} from 'react-bootstrap';

import { useNavigate, useLocation } from 'react-router-dom';

import { toast } from 'react-toastify';

import CustomNavbar from '../components/CustomNavbar';
import TrolleyPicker from '../components/TrolleyPicker';

/* Fallback thumbnail for the order-summary item list */
const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
      <rect width="44" height="44" rx="10" fill="#f3ede1"/>
      <text x="22" y="30" font-size="20" text-anchor="middle" fill="#c2410c">📦</text>
    </svg>`
  );

function CheckoutPage() {
  const navigate = useNavigate();

  const location = useLocation();

  /* Buy Now checkout builds the order from this single item and ignores the
     cart. Adding more products moves it INTO the cart and clears this, which
     turns the page into an ordinary cart checkout. */
  const [buyNowItem, setBuyNowItem] = useState(
    () => location.state?.buyNowItem || null
  );

  const [orderTotal, setOrderTotal] = useState(0);

  /* Cart line items (productId/price/quantity) — needed so the backend can
     scope product-specific coupons to the eligible items. */
  const [cartItems, setCartItems] = useState([]);

  const [availableCoupons, setAvailableCoupons] = useState([]);

  // const [user,
  //   setUser] =
  //   useState({});

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    pincode: ''
  });
  const [couponCode, setCouponCode] = useState('');

  const [discount, setDiscount] = useState(0);

  const [discountAmount, setDiscountAmount] = useState(0);

  /* 'Online' → Razorpay, 'COD' → pay the delivery partner on arrival.
     A COD order is simply an order with no paymentId — that is how the admin
     and delivery dashboards already tell the two apart. */
  const [paymentMethod, setPaymentMethod] = useState('Online');

  const [placing, setPlacing] = useState(false);

  /* Drag-and-drop trolley picker */
  const [showTrolley, setShowTrolley] = useState(false);

  /* Moving a Buy Now item into the cart before the picker opens */
  const [converting, setConverting] = useState(false);

  /* Item list in the summary is collapsed by default — the totals matter more
     here than the line items, and it keeps the summary panel short. */
  const [showItems, setShowItems] = useState(false);

  const fetchAddress = useCallback(async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) return;

      const { data } = await axios.get(
        `${API}/address/${userInfo._id}`
      );

      if (data) {
        setFormData({
          name: data.name || '',

          phone: data.phone || '',

          address: data.address || '',

          city: data.city || '',

          pincode: data.pincode || ''
        });
      }

    } catch (error) {
      console.log(error);
    }
    /* Address only — totals live in loadTotals so switching out of Buy Now
       mode can't re-run this and wipe the shipping form. */
  }, []);

  /* Order lines + total, from whichever source this checkout is using */
  const loadTotals = useCallback(async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) return;

      if (buyNowItem) {
        setOrderTotal(buyNowItem.price * buyNowItem.quantity);

        setCartItems([
          {
            productId: buyNowItem.productId,
            name: buyNowItem.name,
            image: buyNowItem.image || '',
            price: buyNowItem.price,
            quantity: buyNowItem.quantity
          }
        ]);

        return;
      }

      const { data: cart } = await axios.get(
        `${API}/cart/${userInfo._id}`
      );

      setOrderTotal(
        cart.reduce((acc, item) => acc + item.price * item.quantity, 0)
      );

      setCartItems(
        cart.map((item) => ({
          productId: item.productId,
          name: item.name,
          image: item.image,
          price: item.price,
          quantity: item.quantity
        }))
      );
    } catch (error) {
      console.log(error);
    }
  }, [buyNowItem]);

  /* Re-read just the cart after the trolley picker adds something. Deliberately
     not fetchAddress() — that also resets the shipping form, wiping edits. */
  const refreshCart = useCallback(async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) return;

      const { data: cart } = await axios.get(
        `${API}/cart/${userInfo._id}`
      );

      setOrderTotal(
        cart.reduce((acc, item) => acc + item.price * item.quantity, 0)
      );

      setCartItems(
        cart.map((item) => ({
          productId: item.productId,
          name: item.name,
          image: item.image,
          price: item.price,
          quantity: item.quantity
        }))
      );

      /* A discount worked out for the old basket no longer applies — drop it
         rather than charging against a stale figure. */
      setCouponCode((current) => {
        if (current) {
          setDiscount(0);
          setDiscountAmount(0);

          toast.info('Cart changed — please re-apply your coupon');
        }

        return '';
      });
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));

        const { data } = await axios.get(
          `${API}/users/profile`,

          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`
            }
          }
        );

        setFormData({
          name: data.name || '',

          phone: data.phone || '',

          address: data.address || '',

          city: data.city || '',

          pincode: data.pincode || ''
        });
        // setUser(data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchProfile();
    const fetchCoupons = async () => {
      try {
        const { data } = await axios.get(
          `${API}/coupons/active`
        );

        setAvailableCoupons(data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchCoupons();
    fetchAddress();
  }, [fetchAddress]);

  /* Order lines/total load separately from the address, so re-reading the cart
     can never reset the shipping form. */
  useEffect(() => {
    loadTotals();
  }, [loadTotals]);

  /*
    Opening the trolley from a Buy Now checkout: push the Buy Now product into
    the cart first and drop out of Buy Now mode, so the order is built from the
    cart and everything the shopper adds actually reaches it.
  */
  const openTrolley = async () => {
    if (!buyNowItem) {
      setShowTrolley(true);
      return;
    }

    try {
      setConverting(true);

      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) {
        toast.error('Please Login First');
        navigate('/login');
        return;
      }

      await axios.post(`${API}/cart`, {
        userId: userInfo._id,
        productId: buyNowItem.productId,
        name: buyNowItem.name,
        image: buyNowItem.image || '',
        price: buyNowItem.price,
        quantity: buyNowItem.quantity || 1
      });

      /* From here on this is an ordinary cart checkout */
      setBuyNowItem(null);

      setShowTrolley(true);
    } catch (error) {
      console.log(error);

      toast.error(
        error.response?.data?.message || 'Could not open your trolley'
      );
    } finally {
      setConverting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,

      [e.target.name]: e.target.value
    });
  };

  /* The items being ordered — either the single Buy Now item or the cart. */
  const buildOrderItems = async (userInfo) => {
    if (buyNowItem) {
      return [buyNowItem];
    }

    const { data: cart } = await axios.get(
      `${API}/cart/${userInfo._id}`
    );

    return cart.map((item) => ({
      productId: item.productId,

      name: item.name,

      quantity: item.quantity,

      price: item.price
    }));
  };

  /*
    Shared by both payment paths. `payment` is empty for cash on delivery; for
    an online order it carries everything Razorpay returned, because the server
    re-checks the signature before it will write the order.

    No `status` is sent — the server decides that from whether the payment
    verifies, so the browser cannot declare its own order Confirmed.
  */
  const createOrder = async (userInfo, amount, payment = {}) => {
    const orderItems = await buildOrderItems(userInfo);

    await axios.post(
      `${API}/orders`,

      {
        userId: userInfo._id,

        customerName: formData.name,

        phone: formData.phone,

        address: formData.address,

        city: formData.city,

        pincode: formData.pincode,

        orderItems,

        total: amount,

        couponCode,

        discount,

        discountAmount,

        paymentId: payment.paymentId || '',

        razorpayOrderId: payment.orderId || '',

        razorpaySignature: payment.signature || ''
      }
    );

    if (couponCode) {
      await axios.put(`${API}/coupons/use/${couponCode}`);
    }
  };

  const placeOrder = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Please Enter Full Name');

        return;
      }

      if (!formData.phone.trim()) {
        toast.error('Please Enter Phone Number');

        return;
      }

      if (!/^[0-9]{10}$/.test(formData.phone)) {
        toast.error('Phone Number Must Be 10 Digits');

        return;
      }

      if (!formData.address.trim()) {
        toast.error('Please Enter Address');

        return;
      }

      if (!formData.city.trim()) {
        toast.error('Please Enter City');

        return;
      }

      if (!formData.pincode.trim()) {
        toast.error('Please Enter Pincode');

        return;
      }

      if (!/^[0-9]{6}$/.test(formData.pincode)) {
        toast.error('Pincode Must Be 6 Digits');

        return;
      }
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) {
        toast.error('Please Login First');

        navigate('/login');

        return;
      }

      let amount = 0;

      if (buyNowItem) {
        amount = buyNowItem.price * buyNowItem.quantity;
      } else {
        const { data: cartItems } = await axios.get(
          `${API}/cart/${userInfo._id}`
        );

        amount = cartItems.reduce(
          (acc, item) => acc + item.price * item.quantity,

          0
        );
      }
      amount = Math.max(
        amount - discountAmount,

        0
      );

      /* ---- Cash on delivery: no gateway, order goes straight in ---- */
      if (paymentMethod === 'COD') {
        try {
          setPlacing(true);

          await createOrder(userInfo, amount, {});

          toast.success(
            `Order Placed — Pay ₹${amount.toLocaleString()} On Delivery`
          );

          navigate('/orders');
        } catch (error) {
          toast.error(error.response?.data?.message || 'Order Creation Failed');
        } finally {
          setPlacing(false);
        }

        return;
      }

      /* ---- Online payment via Razorpay ---- */
      const { data } = await axios.post(
        `${API}/payment`,

        {
          amount
        }
      );

      const options = {
        /* Sent by the server alongside the order, so switching between test
           and live keys is a backend environment change only. */
        key: data.keyId,

        amount: data.amount,

        currency: data.currency,

        name: 'PoojaMart',

        description: 'Order Payment',

        order_id: data.id,

        handler: async function (response) {
          try {
            /* Ask our own server whether the signature Razorpay returned is
               genuine before telling the customer anything. */
            await axios.post(
              `${API}/payment/verify`,

              {
                razorpay_order_id: response.razorpay_order_id,

                razorpay_payment_id: response.razorpay_payment_id,

                razorpay_signature: response.razorpay_signature
              }
            );

            /* The server verifies again while writing the order, then marks
               it Paid and Confirmed. */
            await createOrder(userInfo, amount, {
              orderId: response.razorpay_order_id,

              paymentId: response.razorpay_payment_id,

              signature: response.razorpay_signature
            });

            toast.success('Payment Verified — Order Confirmed');

            navigate('/orders');
          } catch (error) {
            toast.error(
              error.response?.data?.message ||
                'Payment could not be verified. Your order was not placed.'
            );
          }
        },

        prefill: {
          name: formData.name,

          contact: formData.phone
        },

        theme: {
          color: '#198754'
        }
      };

      const razorpay = new window.Razorpay(options);

      razorpay.open();
    } catch (error) {
      toast.error('Payment Failed');
    }
  };

  const applyCouponCode = async (code) => {
    try {
      const { data } = await axios.post(
        `${API}/coupons/verify`,

        { code, cartItems }
      );

      setCouponCode(code);

      setDiscount(data.discount);

      setDiscountAmount(data.discountAmount);

      toast.success(
        data.productSpecific
          ? `Coupon applied — ₹${data.discountAmount.toLocaleString()} off eligible items`
          : `${data.discount}% OFF Applied`
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid Coupon');
    }
  };
  const finalTotal = orderTotal - discountAmount;

  const isAddressIncomplete =
    !formData.name ||
    !formData.phone ||
    !formData.address ||
    !formData.city ||
    !formData.pincode;

  /* Only surface coupons that actually apply to the cart: store-wide coupons,
     or product-specific coupons whose products are present in the cart. */
  const cartProductIds = new Set(cartItems.map((item) => String(item.productId)));

  const applicableCoupons = availableCoupons.filter(
    (coupon) =>
      !coupon.products ||
      coupon.products.length === 0 ||
      coupon.products.some((id) => cartProductIds.has(String(id)))
  );
  return (
    <>
      <CustomNavbar />

      <div className="checkout-wrap">
        <Container>
          {/* Hero + progress */}
          <Card className="border-0 shadow-lg mb-4 checkout-hero">
            <span className="checkout-hero-blob checkout-hero-blob-1" />
            <span className="checkout-hero-blob checkout-hero-blob-2" />

            {/* Aarti backdrop — a ray fan turning behind a mandala ring that
                turns the other way, so the two never sync up. */}
            <span className="checkout-rays" aria-hidden="true" />
            <span className="checkout-mandala" aria-hidden="true" />

            {/* Om watermark, breathing on the right of the banner */}
            <span className="checkout-om" aria-hidden="true">
              ॐ
            </span>

            {/* Pushpa vrishti — temple flowers raining slowly across the card.
                Each petal gets its own lane, speed and negative delay so the
                fall never looks like a single repeating burst. */}
            <span className="checkout-petals" aria-hidden="true">
              {['🌸', '🌼', '🌸', '🏵️', '🌼', '🌸'].map((petal, i) => (
                <span
                  key={i}
                  className={`checkout-petal checkout-petal-${i + 1}`}
                >
                  {petal}
                </span>
              ))}
            </span>

            <Card.Body className="position-relative p-4 p-md-5">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                  <span className="checkout-eyebrow">Almost there</span>
                  <h1 className="fw-bold mb-1 checkout-hero-title">
                    Secure Checkout
                  </h1>
                  <p className="mb-0 checkout-hero-text">
                    Confirm your delivery details and complete your order.
                  </p>
                </div>

                <div className="checkout-hero-total">
                  <div className="checkout-hero-total-label">Order total</div>
                  <div className="checkout-hero-total-value">
                    ₹{finalTotal.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="checkout-steps">
                {[
                  { n: 1, icon: '🛒', label: 'Cart' },
                  { n: 2, icon: '🚚', label: 'Delivery' },
                  { n: 3, icon: '💳', label: 'Payment' }
                ].map((step, i) => (
                  <div
                    className={`checkout-step${
                      i <= 1 ? ' is-done' : ' is-current'
                    }`}
                    key={step.n}
                  >
                    <span className="checkout-step-dot">{step.icon}</span>
                    <span className="checkout-step-label">{step.label}</span>
                    {i < 2 && <span className="checkout-step-line" />}
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Coupons sit directly under the hero so they're the first thing
              seen, and lay out horizontally to stay one row tall. */}
          {applicableCoupons.length > 0 && (
            <Card className="border-0 shadow-sm mb-4 checkout-coupons-card">
              <Card.Body className="p-3 p-md-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div className="checkout-legend checkout-legend-plain">
                    <span className="checkout-legend-icon">🎟</span>
                    <div>
                      <div className="checkout-legend-title">
                        Available Coupons
                      </div>
                      <small className="checkout-legend-hint">
                        Tap Apply to cut your total
                      </small>
                    </div>
                  </div>

                  <span className="checkout-coupon-count">
                    {applicableCoupons.length} available
                  </span>
                </div>

                <div className="checkout-coupon-strip">
                  {applicableCoupons.map((coupon) => (
                    <div
                      key={coupon._id}
                      className={`checkout-coupon${
                        couponCode === coupon.code ? ' is-applied' : ''
                      }`}
                    >
                      {/* perforated stub, like a real ticket */}
                      <div className="checkout-coupon-stub">
                        <span className="checkout-coupon-pct">
                          {coupon.discountPercentage}%
                        </span>
                        <span className="checkout-coupon-off">OFF</span>
                      </div>

                      <div className="flex-grow-1 min-w-0">
                        <div className="checkout-coupon-code">
                          {coupon.code}
                        </div>

                        {coupon.products && coupon.products.length > 0 ? (
                          <span className="checkout-coupon-tag">
                            Select items only
                          </span>
                        ) : (
                          <span className="checkout-coupon-tag is-all">
                            Whole order
                          </span>
                        )}
                      </div>

                      <Button
                        variant={
                          couponCode === coupon.code ? 'danger' : 'success'
                        }
                        size="sm"
                        className="checkout-coupon-btn"
                        onClick={() => {
                          if (couponCode === coupon.code) {
                            setCouponCode('');

                            setDiscount(0);

                            setDiscountAmount(0);

                            toast.info('Coupon Removed');
                          } else {
                            applyCouponCode(coupon.code);
                          }
                        }}
                      >
                        {couponCode === coupon.code ? 'Remove' : 'Apply'}
                      </Button>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          <Row className="g-4">
            <Col lg={8}>
              {/* h-100 on both cards in this row: the Col is already stretched
                  to the row's height by flexbox, so this makes each card fill
                  its Col and the pair end level whichever side is taller. */}
              <Card
                className="shadow border-0 h-100"
                style={{
                  borderRadius: '25px'
                }}
              >
                <Card.Body className="p-4">
                  {/* Where it's going */}
                  <div className="checkout-deliver mb-4">
                    <span className="checkout-deliver-pin">📍</span>

                    <div className="flex-grow-1 min-w-0">
                      <div className="checkout-deliver-label">
                        Delivering to
                      </div>

                      <div className="checkout-deliver-name">
                        {formData.name || 'Your name'}
                      </div>

                      <div className="checkout-deliver-addr">
                        {formData.address || 'No address yet'}
                        {formData.city ? `, ${formData.city}` : ''}
                        {formData.pincode ? ` - ${formData.pincode}` : ''}
                      </div>

                      {formData.phone && (
                        <div className="checkout-deliver-phone">
                          📞 {formData.phone}
                        </div>
                      )}
                    </div>
                  </div>

                  {isAddressIncomplete && (
                    <div className="checkout-warn mb-4">
                      ⚠ Fill in the highlighted fields below before placing your
                      order.
                    </div>
                  )}

                  <div className="checkout-legend mb-3">
                    <span className="checkout-legend-icon">🚚</span>
                    <div>
                      <div className="checkout-legend-title">
                        Shipping Details
                      </div>
                      <small className="checkout-legend-hint">
                        Where should we deliver this order?
                      </small>
                    </div>
                  </div>

                  <Form className="checkout-form">
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="checkout-label">
                            Full Name <span className="checkout-req">*</span>
                          </Form.Label>

                          <InputGroup
                            className={`checkout-input${
                              formData.name ? '' : ' is-missing'
                            }`}
                          >
                            <InputGroup.Text>🙍</InputGroup.Text>
                            <Form.Control
                              type="text"
                              name="name"
                              placeholder="Who is receiving this?"
                              value={formData.name}
                              onChange={handleChange}
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="checkout-label">
                            Phone Number <span className="checkout-req">*</span>
                          </Form.Label>

                          <InputGroup
                            className={`checkout-input${
                              /^[0-9]{10}$/.test(formData.phone)
                                ? ''
                                : ' is-missing'
                            }`}
                          >
                            <InputGroup.Text>📞</InputGroup.Text>
                            <Form.Control
                              type="text"
                              name="phone"
                              inputMode="numeric"
                              maxLength={10}
                              placeholder="10-digit mobile number"
                              value={formData.phone}
                              onChange={handleChange}
                            />
                          </InputGroup>

                          <small className="checkout-help">
                            For delivery updates and the OTP on arrival
                          </small>
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label className="checkout-label">
                            Address <span className="checkout-req">*</span>
                          </Form.Label>

                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="address"
                            placeholder="House / flat no, street, landmark…"
                            className={`checkout-textarea${
                              formData.address ? '' : ' is-missing'
                            }`}
                            value={formData.address}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="checkout-label">
                            City <span className="checkout-req">*</span>
                          </Form.Label>

                          <InputGroup
                            className={`checkout-input${
                              formData.city ? '' : ' is-missing'
                            }`}
                          >
                            <InputGroup.Text>🏙</InputGroup.Text>
                            <Form.Control
                              type="text"
                              name="city"
                              placeholder="City"
                              value={formData.city}
                              onChange={handleChange}
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="checkout-label">
                            Pincode <span className="checkout-req">*</span>
                          </Form.Label>

                          <InputGroup
                            className={`checkout-input${
                              /^[0-9]{6}$/.test(formData.pincode)
                                ? ''
                                : ' is-missing'
                            }`}
                          >
                            <InputGroup.Text>🔢</InputGroup.Text>
                            <Form.Control
                              type="text"
                              name="pincode"
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="6 digits"
                              value={formData.pincode}
                              onChange={handleChange}
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>

            </Col>

            <Col lg={4}>
              {/* Was position: sticky — dropped, because a card stretched to the
                  full row height has no slack left to scroll within, so the two
                  can't both be true. Equal height won. */}
              <Card
                className="shadow border-0 h-100"
                style={{
                  borderRadius: '25px'
                }}
              >
                <Card.Body className="d-flex flex-column">
                  {/* Header doubles as the show/hide control for the items,
                      so the summary stays short by default. */}
                  <button
                    type="button"
                    className="checkout-sum-head"
                    onClick={() => setShowItems((open) => !open)}
                  >
                    <span className="checkout-sum-title">🛍 Order Summary</span>

                    <span className="checkout-sum-toggle">
                      {cartItems.length} item
                      {cartItems.length === 1 ? '' : 's'}
                      <span
                        className={`checkout-caret${
                          showItems ? ' is-open' : ''
                        }`}
                      >
                        ▾
                      </span>
                    </span>
                  </button>

                  {/* What's actually being ordered */}
                  {showItems && cartItems.length > 0 && (
                    <div className="checkout-items mb-3">
                      {cartItems.map((item, i) => (
                        <div className="checkout-item" key={item.productId || i}>
                          <div className="checkout-item-thumb">
                            <img
                              src={item.image || PLACEHOLDER_IMG}
                              alt={item.name || 'Product'}
                              onError={(e) => {
                                e.currentTarget.src = PLACEHOLDER_IMG;
                              }}
                            />
                            <span className="checkout-item-qty">
                              {item.quantity}
                            </span>
                          </div>

                          <div className="checkout-item-info">
                            <div
                              className="checkout-item-name"
                              title={item.name}
                            >
                              {item.name || 'Product'}
                            </div>
                            <div className="checkout-item-unit">
                              ₹{Number(item.price).toLocaleString()} each
                            </div>
                          </div>

                          <div className="checkout-item-total">
                            ₹{(item.price * item.quantity).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="checkout-money">
                    <div className="checkout-money-row">
                      <span>Subtotal</span>
                      <span>₹{orderTotal.toLocaleString()}</span>
                    </div>

                    {couponCode && (
                      <div className="checkout-money-row is-save">
                        <span>
                          🎟 {couponCode}
                          <button
                            type="button"
                            className="checkout-coupon-clear"
                            title="Remove coupon"
                            onClick={() => {
                              setCouponCode('');
                              setDiscount(0);
                              setDiscountAmount(0);
                              toast.info('Coupon Removed');
                            }}
                          >
                            ✕
                          </button>
                        </span>
                        <span>− ₹{discountAmount.toLocaleString()}</span>
                      </div>
                    )}

                    <div className="checkout-money-row">
                      <span>Delivery</span>
                      <span className="checkout-free">FREE</span>
                    </div>

                    <div className="checkout-money-total">
                      <span>Total to pay</span>
                      <span className="checkout-money-value">
                        ₹{finalTotal.toLocaleString()}
                      </span>
                    </div>

                    {couponCode && discountAmount > 0 && (
                      <div className="checkout-saved">
                        🎉 You saved ₹{discountAmount.toLocaleString()} on this
                        order
                      </div>
                    )}
                  </div>

                  {/* Opens the drag-and-drop trolley picker */}
                  <button
                    type="button"
                    className="checkout-add-more mt-3"
                    disabled={converting}
                    onClick={openTrolley}
                  >
                    <span className="checkout-add-more-icon">🛒</span>
                    <span>
                      <span className="checkout-add-more-title">
                        {converting
                          ? 'Opening your trolley…'
                          : '➕ Add More Products'}
                      </span>
                      <small className="checkout-add-more-sub">
                        Drag items in — up to 5 products
                      </small>
                    </span>
                  </button>

                  {/* Payment method */}
                  <h5 className="fw-bold mt-4 mb-3">💳 Payment Method</h5>

                  <div className="d-flex flex-column gap-2 mb-4">
                    {[
                      {
                        key: 'Online',
                        icon: '💳',
                        title: 'Pay Online',
                        desc: 'UPI, Card, Net Banking & Wallets'
                      },
                      {
                        key: 'COD',
                        icon: '💵',
                        title: 'Cash On Delivery',
                        desc: 'Pay the delivery partner when it arrives'
                      }
                    ].map((method) => (
                      <div
                        key={method.key}
                        className={`pay-option ${
                          paymentMethod === method.key ? 'pay-option-active' : ''
                        }`}
                        onClick={() => setPaymentMethod(method.key)}
                      >
                        <Form.Check
                          type="radio"
                          name="paymentMethod"
                          id={`pay-${method.key}`}
                          className="pay-radio"
                          checked={paymentMethod === method.key}
                          onChange={() => setPaymentMethod(method.key)}
                          label={
                            <span className="d-block">
                              <span className="fw-bold">
                                {method.icon} {method.title}
                              </span>
                              <small className="text-muted d-block">
                                {method.desc}
                              </small>
                            </span>
                          }
                        />
                      </div>
                    ))}
                  </div>

                  {paymentMethod === 'COD' && (
                    <div className="cod-note mb-3">
                      💵 Keep <strong>₹{finalTotal.toLocaleString()}</strong>{' '}
                      ready in cash. You can still cancel from the Orders page
                      until the parcel is out for delivery.
                    </div>
                  )}

                  {/* mt-auto: when this card is the shorter of the two and
                      stretches, the slack collects above the button instead of
                      leaving it floating mid-card. */}
                  <Button
                    variant="success"
                    size="lg"
                    className="w-100 mt-auto"
                    disabled={
                      placing ||
                      !formData.name ||
                      !formData.phone ||
                      !formData.address ||
                      !formData.city ||
                      !formData.pincode
                    }
                    onClick={placeOrder}
                  >
                    {placing
                      ? 'Placing Order…'
                      : paymentMethod === 'COD'
                        ? '💵 Place Order (Cash On Delivery)'
                        : 'Proceed To Payment'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <TrolleyPicker
        show={showTrolley}
        onHide={() => {
          setShowTrolley(false);
          /* Catch removals/changes made while the picker was open */
          refreshCart();
        }}
        onCartChanged={refreshCart}
      />

      <style>{checkoutStyles}</style>
    </>
  );
}

const checkoutStyles = `
  /* Page shell — matches the cart/profile/product pages */
  .checkout-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 44px) 0;
  }
  .min-w-0 { min-width: 0; }

  /* ===== Hero ===== */
  .checkout-hero {
    border-radius: 24px;
    overflow: hidden;
    color: #fff;
    position: relative;
    background: linear-gradient(120deg, #0f172a, #7c2d12 55%, #ea580c 85%, #f59e0b);
    background-size: 220% 220%;
    animation: checkoutDrift 16s ease-in-out infinite;
  }
  @keyframes checkoutDrift {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  /* Everything written in the banner must sit in front of the backdrop */
  .checkout-hero > .card-body { z-index: 3; }

  .checkout-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    pointer-events: none;
  }
  .checkout-hero-blob-1 {
    width: 210px; height: 210px;
    background: #fbbf24;
    top: -90px; left: 16%;
    opacity: 0.45;
    animation: checkoutBlobFloat 9s ease-in-out infinite;
  }
  .checkout-hero-blob-2 {
    width: 190px; height: 190px;
    background: #38bdf8;
    bottom: -100px; right: 8%;
    opacity: 0.3;
    animation: checkoutBlobFloat 11.5s ease-in-out infinite reverse;
  }
  @keyframes checkoutBlobFloat {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50%      { transform: translate(20px, 16px) scale(1.14); }
  }

  /* ===== Divine backdrop ===== */

  /* Sun-ray fan. A repeating conic gradient is one paint rather than dozens of
     elements, and rotating it is cheap because only the transform changes. */
  .checkout-rays {
    position: absolute;
    top: 50%; left: 14%;
    width: 640px; height: 640px;
    margin: -320px 0 0 -320px;
    z-index: 0;
    pointer-events: none;
    background: repeating-conic-gradient(
      from 0deg,
      rgba(253,230,138,0.17) 0deg 4deg,
      transparent 4deg 15deg
    );
    mask-image: radial-gradient(circle, #000 8%, transparent 66%);
    -webkit-mask-image: radial-gradient(circle, #000 8%, transparent 66%);
    animation: checkoutRaysTurn 44s linear infinite;
  }
  @keyframes checkoutRaysTurn {
    to { transform: rotate(360deg); }
  }

  /* Mandala ring — counter-rotating, and glowing on its own slower beat so the
     spin and the pulse drift in and out of phase. */
  .checkout-mandala {
    position: absolute;
    top: 50%; left: 14%;
    width: 300px; height: 300px;
    margin: -150px 0 0 -150px;
    z-index: 0;
    pointer-events: none;
    border-radius: 50%;
    border: 2px dashed rgba(251,191,36,0.45);
    animation:
      checkoutMandalaTurn 30s linear infinite,
      checkoutMandalaGlow 3.8s ease-in-out infinite;
  }
  .checkout-mandala::before {
    content: '';
    position: absolute;
    inset: 38px;
    border-radius: 50%;
    border: 1px solid rgba(253,230,138,0.32);
  }
  @keyframes checkoutMandalaTurn {
    to { transform: rotate(-360deg); }
  }
  @keyframes checkoutMandalaGlow {
    0%, 100% { opacity: 0.3; box-shadow: 0 0 0 0 rgba(251,191,36,0); }
    50%      { opacity: 0.85; box-shadow: 0 0 30px 2px rgba(251,191,36,0.22); }
  }

  /* Om watermark, rising and settling as if on breath */
  .checkout-om {
    position: absolute;
    right: 4%; top: 50%;
    z-index: 0;
    pointer-events: none;
    font-size: clamp(110px, 15vw, 205px);
    line-height: 1;
    color: rgba(253,230,138,0.16);
    text-shadow: 0 0 44px rgba(251,191,36,0.4);
    animation: checkoutOmBreathe 6.5s ease-in-out infinite;
  }
  @keyframes checkoutOmBreathe {
    0%, 100% { opacity: 0.55; transform: translateY(-50%) scale(1); }
    50%      { opacity: 1;    transform: translateY(-52%) scale(1.07); }
  }

  /* Flower rain. Kept behind the copy (z-index 1 against the body's 3) so a
     petal drifting past never sits on top of a word. */
  .checkout-petals {
    position: absolute;
    inset: 0;
    z-index: 1;
    overflow: hidden;
    pointer-events: none;
  }
  .checkout-petal {
    position: absolute;
    top: -34px;
    font-size: 0.85rem;
    opacity: 0;
    animation: checkoutPetalFall 10s linear infinite;
  }
  @keyframes checkoutPetalFall {
    0%   { opacity: 0;    transform: translate3d(0, 0, 0) rotate(0deg); }
    10%  { opacity: 0.85; }
    50%  { transform: translate3d(22px, 170px, 0) rotate(150deg); }
    85%  { opacity: 0.65; }
    100% { opacity: 0;    transform: translate3d(-8px, 350px, 0) rotate(310deg); }
  }
  .checkout-petal-1 { left: 8%;  animation-duration: 9s;    animation-delay: 0s; }
  .checkout-petal-2 { left: 24%; animation-duration: 12s;   animation-delay: -3.2s; font-size: 0.7rem; }
  .checkout-petal-3 { left: 43%; animation-duration: 8.5s;  animation-delay: -6.4s; }
  .checkout-petal-4 { left: 62%; animation-duration: 13s;   animation-delay: -1.6s; font-size: 0.75rem; }
  .checkout-petal-5 { left: 79%; animation-duration: 10.5s; animation-delay: -8.1s; }
  .checkout-petal-6 { left: 92%; animation-duration: 9.5s;  animation-delay: -4.7s; font-size: 0.65rem; }

  .checkout-eyebrow {
    position: relative;
    display: inline-block;
    overflow: hidden;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 2.2px;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: 30px;
    margin-bottom: 10px;
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(255,255,255,0.3);
  }
  /* A band of light crosses the pill, then rests before going again */
  .checkout-eyebrow::after {
    content: '';
    position: absolute;
    top: 0; left: -60%;
    width: 45%; height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.55), transparent);
    transform: skewX(-18deg);
    pointer-events: none;
    animation: checkoutEyebrowWipe 5s ease-in-out infinite;
  }
  @keyframes checkoutEyebrowWipe {
    0%, 62%   { left: -60%; }
    100%      { left: 140%; }
  }

  /* Gilded sweep across the title — same idiom as the Orders banner */
  .checkout-hero-title {
    font-size: clamp(1.7rem, 4vw, 2.5rem);
    letter-spacing: -0.5px;
    background: linear-gradient(100deg, #fff 0%, #fff 38%, #fde68a 46%, #fffbeb 50%, #fde68a 54%, #fff 62%, #fff 100%);
    background-size: 320% 100%;
    background-position: 100% 0;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: checkoutTitleSweep 6.5s ease-in-out infinite;
  }
  @keyframes checkoutTitleSweep {
    0%, 55%   { background-position: 100% 0; }
    92%, 100% { background-position: 0% 0; }
  }
  .checkout-hero-text { opacity: 0.9; font-size: 0.95rem; }
  .checkout-hero-total {
    text-align: right;
    background: rgba(255,255,255,0.14);
    border: 1px solid rgba(255,255,255,0.28);
    border-radius: 16px;
    padding: 12px 20px;
    backdrop-filter: blur(4px);
    animation: checkoutTotalGlow 4.5s ease-in-out infinite;
  }
  @keyframes checkoutTotalGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0); }
    50%      { box-shadow: 0 0 26px 0 rgba(251,191,36,0.34); }
  }
  .checkout-hero-total-label {
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 1px;
    text-transform: uppercase;
    opacity: 0.85;
  }
  .checkout-hero-total-value {
    font-size: 1.7rem;
    font-weight: 800;
    line-height: 1.2;
  }

  /* Step rail */
  .checkout-steps {
    display: flex;
    align-items: center;
    gap: 0;
    margin-top: 26px;
    max-width: 460px;
  }
  .checkout-step {
    position: relative;
    display: flex;
    align-items: center;
    gap: 9px;
    flex: 1 1 auto;
  }
  .checkout-step-dot {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    border-radius: 50%;
    background: rgba(255,255,255,0.18);
    border: 2px solid rgba(255,255,255,0.4);
  }
  .checkout-step.is-done .checkout-step-dot {
    background: #22c55e;
    border-color: #bbf7d0;
  }
  .checkout-step.is-current .checkout-step-dot {
    background: #fbbf24;
    border-color: #fff;
    color: #451a03;
    animation: checkoutPulse 2s ease-in-out infinite;
  }
  @keyframes checkoutPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.55); }
    50%      { box-shadow: 0 0 0 9px rgba(251,191,36,0); }
  }
  .checkout-step-label {
    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .checkout-step-line {
    flex: 1 1 auto;
    height: 2px;
    min-width: 14px;
    margin: 0 8px;
    border-radius: 2px;
    background: rgba(255,255,255,0.32);
  }

  /* ===== Delivering-to card ===== */
  .checkout-deliver {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    padding: 14px 16px;
    border-radius: 16px;
    background: linear-gradient(135deg, #fffbeb, #fff7ed);
    border: 1px solid #fed7aa;
  }
  .checkout-deliver-pin {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    border-radius: 13px;
    background: #fff;
    border: 1px solid #fed7aa;
  }
  .checkout-deliver-label {
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #a16207;
  }
  .checkout-deliver-name {
    font-weight: 800;
    color: #1c1917;
    line-height: 1.3;
  }
  .checkout-deliver-addr, .checkout-deliver-phone {
    font-size: 0.85rem;
    color: #78716c;
    line-height: 1.45;
  }

  .checkout-warn {
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
    border-radius: 12px;
    padding: 11px 15px;
    font-size: 0.86rem;
    font-weight: 600;
  }

  /* ===== Form ===== */
  .checkout-legend {
    display: flex;
    align-items: center;
    gap: 11px;
    padding-bottom: 12px;
    border-bottom: 1px dashed #f1e4d0;
  }
  .checkout-legend-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    border-radius: 13px;
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    border: 1px solid #fed7aa;
  }
  .checkout-legend-title {
    font-weight: 800;
    font-size: 1.05rem;
    color: #7c2d12;
    line-height: 1.2;
  }
  .checkout-legend-hint { color: #a8a29e; font-size: 0.76rem; }

  .checkout-label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.74rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #78716c;
    margin-bottom: 6px;
  }
  .checkout-req { color: #dc2626; }
  .checkout-input {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #ece7df;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .checkout-input .input-group-text {
    background: #fff7ed;
    border: none;
    color: #c2410c;
    padding-left: 14px;
    padding-right: 12px;
  }
  .checkout-input .form-control {
    border: none;
    padding: 11px 14px;
    box-shadow: none !important;
  }
  .checkout-input:focus-within {
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234,88,12,0.14);
  }
  /* a field the order can't be placed without */
  .checkout-input.is-missing { border-color: #fdba74; }
  .checkout-textarea {
    border-radius: 12px;
    border: 1px solid #ece7df;
    padding: 11px 14px;
  }
  .checkout-textarea.is-missing { border-color: #fdba74; }
  .checkout-textarea:focus {
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234,88,12,0.14);
  }
  .checkout-help {
    display: block;
    margin-top: 5px;
    font-size: 0.7rem;
    color: #a8a29e;
  }

  /* ===== Coupon tickets ===== */
  .checkout-coupon {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    margin-bottom: 10px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid #f1e4d0;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }
  .checkout-coupon:hover {
    transform: translateY(-2px);
    border-color: #fdba74;
    box-shadow: 0 10px 20px rgba(234,88,12,0.14);
  }
  .checkout-coupon.is-applied {
    border-color: #86efac;
    background: #f0fdf4;
  }
  .checkout-coupon-stub {
    position: relative;
    width: 58px;
    flex-shrink: 0;
    text-align: center;
    padding: 8px 4px;
    border-radius: 11px;
    color: #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
  }
  .checkout-coupon.is-applied .checkout-coupon-stub {
    background: linear-gradient(135deg, #16a34a, #22c55e);
  }
  /* the notch that makes it read as a torn ticket */
  .checkout-coupon-stub::after {
    content: '';
    position: absolute;
    top: 50%;
    right: -13px;
    width: 12px;
    height: 12px;
    transform: translateY(-50%);
    border-radius: 50%;
    background: #fff;
    border: 1px solid #f1e4d0;
  }
  .checkout-coupon.is-applied .checkout-coupon-stub::after { background: #f0fdf4; }
  .checkout-coupon-pct {
    display: block;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1;
  }
  .checkout-coupon-off {
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 1px;
  }
  .checkout-coupon-code {
    font-weight: 800;
    color: #1c1917;
    letter-spacing: 0.5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .checkout-coupon-tag {
    font-size: 0.65rem;
    font-weight: 700;
    color: #b45309;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 20px;
    padding: 2px 8px;
  }
  .checkout-coupon-tag.is-all {
    color: #15803d;
    background: #f0fdf4;
    border-color: #bbf7d0;
  }
  .checkout-coupon-btn { border-radius: 9px; font-weight: 700; }

  /* ===== Compact summary ===== */
  .checkout-sum-head {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 0 0 12px;
    margin-bottom: 12px;
    border: none;
    border-bottom: 1px dashed #f1e4d0;
    background: transparent;
    color: inherit;
  }
  .checkout-sum-title {
    font-size: 1.15rem;
    font-weight: 800;
    color: #7c2d12;
  }
  .checkout-sum-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.76rem;
    font-weight: 700;
    color: #a16207;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 30px;
    padding: 4px 11px;
    white-space: nowrap;
  }
  .checkout-caret {
    display: inline-block;
    transition: transform 0.2s ease;
  }
  .checkout-caret.is-open { transform: rotate(180deg); }

  /* One money block instead of three stacked ones */
  .checkout-money {
    border-radius: 16px;
    border: 1px solid #f1e4d0;
    background: #fdfcfa;
    padding: 14px 16px;
  }
  .checkout-money-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 0.92rem;
    color: #57534e;
    padding: 5px 0;
  }
  .checkout-money-row span:last-child {
    font-weight: 700;
    color: #1c1917;
    font-variant-numeric: tabular-nums;
  }
  .checkout-money-row.is-save,
  .checkout-money-row.is-save span:last-child {
    color: #15803d;
    font-weight: 700;
  }
  .checkout-free { color: #15803d !important; font-weight: 800 !important; }
  .checkout-coupon-clear {
    border: none;
    background: transparent;
    color: #a8a29e;
    font-size: 0.7rem;
    font-weight: 800;
    margin-left: 6px;
    padding: 0 4px;
    line-height: 1;
  }
  .checkout-coupon-clear:hover { color: #b91c1c; }

  .checkout-money-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
    padding-top: 12px;
    border-top: 2px solid #f1e4d0;
    font-size: 1.05rem;
    font-weight: 800;
    color: #1c1917;
  }
  .checkout-money-value {
    font-size: 1.85rem;
    font-weight: 800;
    line-height: 1;
    background: linear-gradient(120deg, #15803d, #22c55e);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-variant-numeric: tabular-nums;
  }
  .checkout-saved {
    margin-top: 10px;
    text-align: center;
    font-size: 0.78rem;
    font-weight: 700;
    color: #15803d;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 30px;
    padding: 6px 12px;
  }

  /* Coupon strip under the hero */
  .checkout-coupons-card {
    border-radius: 20px;
    background: linear-gradient(135deg, #fffdf7, #fff9ef);
    border: 1px solid #fde68a !important;
  }
  .checkout-legend-plain {
    border: none;
    padding-bottom: 0;
    margin-bottom: 0;
  }
  .checkout-coupon-count {
    font-size: 0.72rem;
    font-weight: 800;
    color: #a16207;
    background: #fff;
    border: 1px solid #fde68a;
    border-radius: 30px;
    padding: 5px 13px;
    white-space: nowrap;
  }
  /* Tickets side by side; they wrap rather than stack one per row */
  .checkout-coupon-strip {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 10px;
  }
  .checkout-coupon-strip .checkout-coupon { margin-bottom: 0; }

  /* ===== Order items preview ===== */
  .checkout-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 230px;
    overflow-y: auto;
    padding-right: 4px;
  }
  .checkout-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-radius: 12px;
    background: #fdfcfa;
    border: 1px solid #f3efe8;
  }
  .checkout-item-thumb {
    position: relative;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
  }
  .checkout-item-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
    border: 1px solid #f1e4d0;
    background: #fff;
  }
  .checkout-item-qty {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 19px;
    height: 19px;
    padding: 0 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 30px;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    color: #fff;
    font-size: 0.63rem;
    font-weight: 800;
    border: 2px solid #fff;
  }
  .checkout-item-info { flex: 1 1 auto; min-width: 0; }
  .checkout-item-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: #1c1917;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .checkout-item-unit { font-size: 0.72rem; color: #a8a29e; }
  .checkout-item-total {
    font-weight: 800;
    font-size: 0.9rem;
    color: #15803d;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  @media (max-width: 575.98px) {
    .checkout-hero-total { width: 100%; text-align: left; }
    .checkout-step-label { display: none; }
    .checkout-steps { max-width: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    .checkout-hero, .checkout-step.is-current .checkout-step-dot,
    .checkout-coupon, .checkout-input,
    .checkout-hero-blob-1, .checkout-hero-blob-2,
    .checkout-rays, .checkout-mandala, .checkout-om,
    .checkout-eyebrow::after, .checkout-hero-title,
    .checkout-hero-total { animation: none; transition: none; }
    /* Falling petals have nothing meaningful to say when they can't fall */
    .checkout-petals { display: none; }
    /* The clipped gradient leaves the heading invisible without its sweep */
    .checkout-hero-title {
      color: #fff;
      -webkit-text-fill-color: #fff;
    }
  }

  /* Opens the drag-and-drop trolley picker */
  .checkout-add-more {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
    padding: 11px 14px;
    border-radius: 14px;
    background: linear-gradient(135deg, #fffbeb, #fff7ed);
    border: 2px dashed #fdba74;
    color: #7c2d12;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  }
  .checkout-add-more:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #fff;
    border-color: #ea580c;
    box-shadow: 0 12px 24px rgba(234,88,12,0.20);
  }
  .checkout-add-more:disabled { opacity: 0.7; }
  .checkout-add-more-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    border-radius: 13px;
    background: #fff;
    border: 1px solid #fed7aa;
    transition: transform 0.25s ease;
  }
  .checkout-add-more:hover:not(:disabled) .checkout-add-more-icon {
    transform: scale(1.12) rotate(-8deg);
  }
  .checkout-add-more-title {
    display: block;
    font-weight: 800;
    font-size: 0.9rem;
    line-height: 1.2;
  }
  .checkout-add-more-sub {
    display: block;
    color: #a16207;
    font-size: 0.74rem;
  }

  @media (prefers-reduced-motion: reduce) {
    .checkout-add-more, .checkout-add-more-icon { transition: none; }
  }

  /* Payment method chooser */
  .pay-option {
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 12px 14px;
    cursor: pointer;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .pay-option:hover {
    border-color: #86efac;
    background: #f8fffb;
  }
  .pay-option-active {
    border-color: #22c55e;
    background: #ecfdf5;
    box-shadow: 0 6px 16px rgba(34, 197, 94, 0.16);
  }
  .pay-option .form-check {
    margin-bottom: 0;
  }
  .pay-option .form-check-input {
    margin-top: 0.35rem;
    cursor: pointer;
  }
  .pay-option .form-check-input:checked {
    background-color: #22c55e;
    border-color: #22c55e;
  }
  .pay-option .form-check-label {
    cursor: pointer;
    width: 100%;
  }

  .cod-note {
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 0.86rem;
  }
`;

export default CheckoutPage;
